// noinspection DuplicatedCode
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { Colors } from "../../../assets/Colors";
import { Card, Button } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import { useQuery } from "@tanstack/react-query";
import { fetchGet, fetchPost } from "../../../fetchers";
import ModalBayar from "../../../components/form/ModalBayar";
import { toast } from "react-toastify";
import { maxPageGlobal } from "../../../assets/config";
import DataTable from "react-data-table-component";
import debounce from "lodash/debounce";
import { BiSearch, BiRefresh, BiFilter } from "react-icons/bi";
import { BsFileEarmarkExcelFill } from "react-icons/bs";
import Skeltons from "../../../components/loading/Skeltons";

const MonitoringPattyCashHO = () => {
    const navigate = useNavigate();

    // State management - Mengikuti pola MonitoringAP
    const [searchInput, setSearchInput] = useState(""); // Untuk input field (langsung update)
    const [searchQuery, setSearchQuery] = useState(""); // Untuk query ke API (debounced)
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(maxPageGlobal);
    const [periode, setPeriode] = useState({
        bulan: (new Date().getMonth() + 1).toString(),
        tahun: new Date().getFullYear().toString(),
    });
    const [isExporting, setIsExporting] = useState(false);
    const [isLibraryReady, setIsLibraryReady] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [isSearching, setIsSearching] = useState(false);

    // Form handling untuk filter
    const initialState = {
        Bulan: new Date(),
        BisnisUnitID: "U0001", // Fixed untuk HO
    };

    const validations = [];

    const { values, errors, touched, customHandler } = useForm(
        initialState,
        validations,
    );

    // Load Excel library dynamically
    useEffect(() => {
        if (window.XLSX) {
            setIsLibraryReady(true);
            return;
        }

        const loadLibrary = async () => {
            try {
                const script = document.createElement("script");
                script.src =
                    "https://cdn.sheetjs.com/xlsx-0.20.2/package/dist/xlsx.full.min.js";
                script.async = true;

                script.onload = () => {
                    console.log("Excel library loaded successfully");
                    setIsLibraryReady(true);
                };

                script.onerror = () => {
                    const altScript = document.createElement("script");
                    altScript.src =
                        "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
                    altScript.async = true;

                    altScript.onload = () => {
                        console.log("Excel library loaded from alternative CDN");
                        setIsLibraryReady(true);
                    };

                    altScript.onerror = () => {
                        console.error("Failed to load Excel library");
                        toast.error(
                            "Gagal memuat library Excel. Silakan refresh halaman.",
                            {
                                position: "top-center",
                                autoClose: 5000,
                            },
                        );
                    };

                    document.body.appendChild(altScript);
                };

                document.body.appendChild(script);
            } catch (error) {
                console.error("Error loading library:", error);
            }
        };

        loadLibrary();
    }, []);

    // Debounced search - Mengikuti pola MonitoringAP
    const debouncedSetSearchQuery = useMemo(
        () =>
            debounce((value) => {
                console.log("Debounced value:", value);
                setSearchQuery(value);
                setPage(1);
                setIsSearching(false);
            }, 800),
        [],
    );

    // Cleanup debounce
    useEffect(() => {
        return () => {
            debouncedSetSearchQuery.cancel();
        };
    }, [debouncedSetSearchQuery]);

    // Handler untuk search input
    const handleSearchChange = useCallback(
        (e) => {
            const value = e.target.value;
            setSearchInput(value);

            debouncedSetSearchQuery.cancel();

            if (value.trim() === "") {
                setSearchQuery("");
                setPage(1);
                setIsSearching(false);
            } else {
                setIsSearching(true);
                debouncedSetSearchQuery(value);
            }
        },
        [debouncedSetSearchQuery],
    );

    // Handler untuk clear search
    const handleClearSearch = useCallback(() => {
        debouncedSetSearchQuery.cancel();
        setSearchInput("");
        setSearchQuery("");
        setPage(1);
        setIsSearching(false);

        toast.info("Pencarian dibersihkan", {
            position: "top-center",
            autoClose: 800,
        });
    }, [debouncedSetSearchQuery]);

    // Update periode ketika nilai form berubah
    useEffect(() => {
        if (values.Bulan) {
            const selectedDate = new Date(values.Bulan);
            const bulan = (selectedDate.getMonth() + 1).toString();
            const tahun = selectedDate.getFullYear().toString();

            setPeriode({ bulan, tahun });
            setPage(1);
        }
    }, [values.Bulan]);

    // Fetch data dengan React Query - Mengikuti pola MonitoringAP
    const {
        data: monitoringData,
        isLoading,
        isFetching,
        refetch,
        error,
    } = useQuery({
        queryKey: [
            "monitoringPattyCashHO",
            periode.bulan,
            periode.tahun,
            page,
            limit,
            searchQuery,
        ],
        queryFn: async () => {
            const endpoint = `Accounting/Mon/CashJournal/U0001/pettycash/${periode.bulan}/${periode.tahun}`;

            const params = new URLSearchParams();

            if (searchQuery && searchQuery.trim() !== "") {
                params.append("search", searchQuery.trim());
            }

            params.append("page", page.toString());
            params.append("limit", limit.toString());

            const url = `${endpoint}?${params.toString()}`;
            console.log("Fetching:", url);

            const response = await fetchGet(url);

            if (isFirstLoad) {
                setIsFirstLoad(false);
            }

            return response;
        },
        keepPreviousData: true,
        staleTime: 30000,
        enabled: true,
    });

    // Function to fetch ALL data for export
    const fetchAllDataForExport = useCallback(async () => {
        try {
            const endpoint = `Accounting/Mon/CashJournal/U0001/pettycash/${periode.bulan}/${periode.tahun}`;

            const params = new URLSearchParams();
            if (searchQuery && searchQuery.trim() !== "") {
                params.append("search", searchQuery.trim());
            }
            params.append("page", "1");
            params.append("limit", "1");

            const firstPage = await fetchGet(`${endpoint}?${params.toString()}`);
            const totalCount = firstPage.count || 0;

            if (totalCount === 0) {
                return [];
            }

            const chunkSize = 1000;
            const totalChunks = Math.ceil(totalCount / chunkSize);
            let allData = [];

            const toastId = toast.loading(`Mengumpulkan data 0/${totalCount}...`, {
                position: "top-center",
            });

            for (let i = 0; i < totalChunks; i++) {
                const chunkParams = new URLSearchParams();
                if (searchQuery && searchQuery.trim() !== "") {
                    chunkParams.append("search", searchQuery.trim());
                }
                chunkParams.append("page", (i + 1).toString());
                chunkParams.append("limit", chunkSize.toString());

                const response = await fetchGet(
                    `${endpoint}?${chunkParams.toString()}`,
                );
                allData = [...allData, ...(response.data || [])];

                toast.update(toastId, {
                    render: `Mengumpulkan data ${Math.min(allData.length, totalCount)}/${totalCount}...`,
                });
            }

            toast.dismiss(toastId);
            return allData;
        } catch (error) {
            console.error("Error fetching all data:", error);
            toast.dismiss();
            throw error;
        }
    }, [periode, searchQuery]);

    // Export to Excel function
    const handleExportExcel = useCallback(async () => {
        if (!isLibraryReady) {
            toast.error(
                "Library Excel sedang dimuat. Silakan tunggu beberapa saat.",
                {
                    position: "top-center",
                    autoClose: 3000,
                },
            );
            return;
        }

        if (typeof window.XLSX === "undefined") {
            toast.error("Library Excel tidak tersedia. Silakan refresh halaman.", {
                position: "top-center",
                autoClose: 3000,
            });
            return;
        }

        try {
            setIsExporting(true);

            const filterInfo = [];
            if (searchQuery && searchQuery.trim() !== "") {
                filterInfo.push(`Search: "${searchQuery}"`);
            }
            filterInfo.push(`Periode: Bulan ${periode.bulan} Tahun ${periode.tahun}`);

            toast.info(`Mengexport data dengan filter: ${filterInfo.join(", ")}`, {
                position: "top-center",
                autoClose: 2000,
            });

            const allData = await fetchAllDataForExport();

            if (!allData || allData.length === 0) {
                toast.warn("Tidak ada data untuk di-export", {
                    position: "top-center",
                    autoClose: 2000,
                });
                return;
            }

            toast.info(`Menyiapkan ${allData.length} data untuk export...`, {
                position: "top-center",
                autoClose: 2000,
            });

            // Format currency untuk export
            const formatCurrency = (value) => {
                if (!value || value === "0" || value === "0.00") return "Rp 0";
                const num = parseFloat(value);
                if (isNaN(num)) return "Rp 0";

                return new Intl.NumberFormat("id-ID", {
                    style: "currency",
                    currency: "IDR",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                }).format(num);
            };

            // Map status untuk export
            const mapStatus = (status) => {
                if (!status) return "Pending";
                const statusStr = status.toString().toLowerCase();
                const statusMap = {
                    "-1": "Menunggu Dikirim",
                    "0": "Menunggu Approval",
                    "1": "Approved",
                    "2": "Revisi",
                    "3": "Ditolak",
                    "pending": "Pending",
                    "approved": "Approved",
                    "rejected": "Rejected",
                };
                return statusMap[statusStr] || status;
            };

            // Prepare data for Excel
            const excelData = allData.map((item, index) => ({
                No: index + 1,
                "KAS BANK ID": item.CashJournalID || "",
                Kepada: item.DariKepada || "",
                "Jenis Transaksi": item.JenisTransaksi || "",
                Bagian: item.BagianID || "",
                Tanggal: item.Tanggal
                    ? new Date(item.Tanggal).toLocaleDateString("id-ID")
                    : "",
                Keterangan: item.Keterangan || "",
                Jurnal: item.JenisCashJournal || "",
                Nominal: formatCurrency(item.Nominal),
                Status: mapStatus(item.Status),
            }));

            const XLSX = window.XLSX;
            const ws = XLSX.utils.json_to_sheet(excelData);

            const colWidths = [
                { wch: 5 },   // No
                { wch: 18 },  // KAS BANK ID
                { wch: 25 },  // Kepada
                { wch: 15 },  // Jenis Transaksi
                { wch: 15 },  // Bagian
                { wch: 12 },  // Tanggal
                { wch: 30 },  // Keterangan
                { wch: 12 },  // Jurnal
                { wch: 20 },  // Nominal
                { wch: 15 },  // Status
            ];
            ws["!cols"] = colWidths;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Patty Cash HO");

            const timestamp = new Date()
                .toISOString()
                .slice(0, 19)
                .replace(/:/g, "-");
            const filename = `Monitoring_PattyCash_HO_${timestamp}.xlsx`;

            XLSX.writeFile(wb, filename);

            toast.success(`Berhasil export ${allData.length} data!`, {
                position: "top-center",
                autoClose: 3000,
            });
        } catch (error) {
            console.error("Export error:", error);
            toast.error(
                "Gagal melakukan export data: " + (error.message || "Unknown error"),
                {
                    position: "top-center",
                    autoClose: 3000,
                },
            );
        } finally {
            setIsExporting(false);
        }
    }, [fetchAllDataForExport, isLibraryReady, searchQuery, periode]);

    // Handler untuk filter
    const handleFilter = useCallback(() => {
        const selectedDate = new Date(values.Bulan);
        const bulan = (selectedDate.getMonth() + 1).toString();
        const tahun = selectedDate.getFullYear().toString();

        setPeriode({ bulan, tahun });
        setPage(1);
        setSearchInput("");
        setSearchQuery("");
        setIsSearching(false);
        debouncedSetSearchQuery.cancel();

        toast.success(`Menampilkan data periode Bulan ${bulan} Tahun ${tahun}`, {
            position: "top-center",
            autoClose: 1000,
        });
    }, [values, debouncedSetSearchQuery]);

    // Handler untuk pagination
    const handlePageChange = useCallback((newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Handler untuk change limit per page
    const handlePerRowsChange = useCallback((newLimit, page) => {
        setLimit(newLimit === -1 ? 999999 : newLimit);
        setPage(1);
    }, []);

    // Handler untuk refresh
    const handleRefresh = useCallback(() => {
        refetch();
        toast.info("Memuat ulang data...", {
            position: "top-center",
            autoClose: 1000,
        });
    }, [refetch]);

    // Format currency
    const formatCurrency = useCallback((value) => {
        if (!value || value === "0" || value === "0.00") return "Rp 0";
        const num = parseFloat(value);
        if (isNaN(num)) return "Rp 0";

        return new Intl.NumberFormat("id-ID", {
            style: "currency",
            currency: "IDR",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(num);
    }, []);

    // Format tanggal
    const formatDate = useCallback((dateString) => {
        if (!dateString) return "-";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleDateString("id-ID", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
        } catch {
            return dateString;
        }
    }, []);

    // Map status
    const mapStatus = useCallback((status) => {
        if (!status) return { value: "Pending", isApproved: false, isRejected: false };

        const statusStr = status.toString().toLowerCase();

        const statusMap = {
            "-1": { value: "Menunggu Dikirim", isApproved: false, isRejected: false },
            "0": { value: "Menunggu Approval", isApproved: false, isRejected: false },
            "1": { value: "Approved", isApproved: true, isRejected: false },
            "2": { value: "Revisi", isApproved: false, isRejected: false },
            "3": { value: "Ditolak", isApproved: false, isRejected: true },
            "pending": { value: "Pending", isApproved: false, isRejected: false },
            "approved": { value: "Approved", isApproved: true, isRejected: false },
            "rejected": { value: "Rejected", isApproved: false, isRejected: true },
        };

        return statusMap[statusStr] || {
            value: status,
            isApproved: false,
            isRejected: false,
        };
    }, []);

    // Navigasi ke detail
    // Modal "Bayar" — sejak jurnal terbit saat pembayaran (bukan saat approval
    // final), tombol inilah yang benar-benar menerbitkan jurnal.
    const [bayarModal, setBayarModal] = useState({ show: false, id: "", nominal: "" });
    const [bayarBusy, setBayarBusy] = useState(false);

    const handleSimpanBayar = useCallback(
        async (tanggal) => {
            setBayarBusy(true);
            try {
                // fetchPost memunculkan toast sukses/gagal sendiri dan MELEMPAR
                // error saat gagal — tak perlu toast tambahan di sini.
                await fetchPost("Accounting/CashJournal/bayar", {
                    ID: bayarModal.id,
                    TanggalBayar: tanggal,
                });
                setBayarModal({ show: false, id: "", nominal: "" });
                refetch();
            } catch (_) {
                // Modal dibiarkan terbuka saat gagal: penyebab tersering adalah
                // detail lawan kosong / tidak seimbang.
            } finally {
                setBayarBusy(false);
            }
        },
        [bayarModal.id, refetch]
    );

    const handleViewDetail = useCallback((cashJournalId, e) => {
        if (e) e.stopPropagation();
        if (!cashJournalId) return;
        const formattedId = cashJournalId.toString().replace(/\//g, "-");
        navigate(`/finance/detail/form-kas-bank/${formattedId}`);
    }, [navigate]);

    // Navigasi ke blanko
    const handleViewBlanko = useCallback((cashJournalId, e) => {
        if (e) e.stopPropagation();
        if (!cashJournalId) return;
        const formattedId = cashJournalId.toString().replace(/\//g, "-");
        navigate(`/finance/report/blanco/pembayaran-non-po/${formattedId}`);
    }, [navigate]);

    // Data preparation
    const tableData = monitoringData?.data || [];
    const totalRows = monitoringData?.count || 0;
    const isLoadingData = isLoading || isFetching;

    // Render export button
    const renderExportButton = () => {
        if (!isLibraryReady) {
            return (
                <Button
                    color="gray"
                    buttonType="filled"
                    size="sm"
                    disabled={true}
                    className="flex items-center px-4 py-2"
                >
                    <svg
                        className="w-4 h-4 mr-2 animate-spin"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                    </svg>
                    Memuat Library...
                </Button>
            );
        }

        return (
            <Button
                color="green"
                buttonType="filled"
                size="sm"
                onClick={handleExportExcel}
                disabled={isExporting || isLoadingData || totalRows === 0}
                className="flex items-center px-4 py-2"
            >
                {isExporting ? (
                    <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Mengexport...
                    </>
                ) : (
                    <>
                        <BsFileEarmarkExcelFill className="mr-2" />
                        Export Excel
                    </>
                )}
            </Button>
        );
    };

    // Columns definition
    const columns = useMemo(() => [
        {
            name: "No",
            selector: (row, index) => (page - 1) * limit + index + 1,
            sortable: false,
            width: "70px",
            center: true,
        },
        {
            name: "KAS BANK ID",
            selector: (row) => row.CashJournalID,
            sortable: true,
            minWidth: "150px",
            cell: row => (
                <div className="text-xs font-mono whitespace-normal break-all">
                    {row.CashJournalID || "-"}
                </div>
            ),
        },
        {
            name: "Kepada",
            selector: (row) => row.DariKepada,
            sortable: true,
            minWidth: "150px",
            cell: row => (
                <div className="text-xs whitespace-normal break-words">
                    {row.DariKepada || "-"}
                </div>
            ),
        },
        {
            name: "Jenis Transaksi",
            selector: (row) => row.JenisTransaksi,
            sortable: true,
            minWidth: "130px",
            cell: row => (
                <div className="text-xs whitespace-normal break-words">
                    {row.JenisTransaksi || "-"}
                </div>
            ),
        },
        {
            name: "Bagian",
            selector: (row) => row.BagianID,
            sortable: true,
            minWidth: "120px",
            cell: row => (
                <div className="text-xs whitespace-normal break-words">
                    {row.BagianID || "-"}
                </div>
            ),
        },
        {
            name: "Tanggal",
            selector: (row) => row.Tanggal,
            sortable: true,
            minWidth: "100px",
            cell: row => (
                <div className="text-xs whitespace-nowrap">
                    {formatDate(row.Tanggal)}
                </div>
            ),
        },
        {
            name: "Keterangan",
            selector: (row) => row.Keterangan,
            sortable: true,
            minWidth: "200px",
            cell: row => (
                <div className="text-xs whitespace-normal break-words">
                    {row.Keterangan || "-"}
                </div>
            ),
        },
        {
            name: "Nominal",
            selector: (row) => row.Nominal,
            sortable: true,
            minWidth: "130px",
            right: true,
            cell: row => (
                <div className="text-xs font-medium text-right font-mono">
                    {formatCurrency(row.Nominal)}
                </div>
            ),
        },
        {
            name: "Status",
            selector: (row) => row.Status,
            sortable: true,
            width: "120px",
            cell: row => {
                const { value: displayStatus, isApproved, isRejected } = mapStatus(row.Status);

                // "Terbayar" bukan angka status tersendiri, melainkan turunan dari
                // TanggalBayar — sejak jurnal terbit saat pembayaran, kolom itulah
                // penanda uang sudah benar-benar bergerak.
                if (isApproved && row.TanggalBayar) {
                    return (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                            Terbayar {row.TanggalBayar}
                        </span>
                    );
                }

                return (
                    <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isApproved
                                ? "bg-green-100 text-green-800 border border-green-200"
                                : isRejected
                                    ? "bg-red-100 text-red-800 border border-red-200"
                                    : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                        }`}
                    >
                        {displayStatus}
                    </span>
                );
            },
        },
        {
            name: "Aksi",
            cell: (row) => {
                const cashJournalId = row.CashJournalID || "";
                const { isApproved } = mapStatus(row.Status);

                return (
                    <div className="flex flex-wrap gap-2">
                        {/* Bayar hanya untuk yang sudah disetujui tapi belum
                            dibayar. Di sinilah jurnalnya terbit. */}
                        {isApproved && !row.TanggalBayar && (
                            <button
                                onClick={() =>
                                    setBayarModal({
                                        show: true,
                                        id: cashJournalId,
                                        nominal: row.Nominal,
                                        tanggalDokumen: row.Tanggal,
                                    })
                                }
                                className="border text-white bg-blue-600 hover:bg-white hover:border-blue-600 hover:text-blue-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-1.5 focus:outline-none transition-colors duration-200"
                                title="Catat pembayaran — jurnal terbit pada tanggal yang dipilih"
                            >
                                Bayar
                            </button>
                        )}
                        <button
                            onClick={(e) => handleViewDetail(cashJournalId, e)}
                            className="border text-white bg-main-100 hover:bg-white hover:border-main-100 hover:text-main-100 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-1.5 focus:outline-none transition-colors duration-200"
                            title="Lihat Detail"
                        >
                            Lihat
                        </button>
                        {isApproved && (
                            <button
                                onClick={(e) => handleViewBlanko(cashJournalId, e)}
                                className="border text-white bg-green-600 hover:bg-white hover:border-green-600 hover:text-green-600 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-3 py-1.5 focus:outline-none transition-colors duration-200"
                                title="Cetak Blanko"
                            >
                                Blanko
                            </button>
                        )}
                    </div>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            width: "140px",
        },
    ], [page, limit, formatDate, formatCurrency, mapStatus, handleViewDetail, handleViewBlanko]);

    // Custom styles untuk DataTable
    const customStyles = useMemo(() => ({
        head: {
            style: {
                backgroundColor: "#f8f9fa",
                fontWeight: "bold",
                fontSize: "12px",
                color: "#374151",
                textTransform: "uppercase",
            },
        },
        headCells: {
            style: {
                paddingLeft: "12px",
                paddingRight: "12px",
                whiteSpace: "nowrap",
                borderBottom: "2px solid #e5e7eb",
                backgroundColor: "#f8f9fa",
                fontWeight: "bold",
                fontSize: "12px",
            },
        },
        cells: {
            style: {
                fontSize: "12px",
                paddingLeft: "12px",
                paddingRight: "12px",
                whiteSpace: "normal",
                wordBreak: "break-word",
                minHeight: "50px",
                verticalAlign: "top",
                borderBottom: "1px solid #f3f4f6",
            },
        },
        rows: {
            style: {
                minHeight: "50px",
                "&:nth-of-type(even)": {
                    backgroundColor: "#f9fafb",
                },
                "&:hover": {
                    backgroundColor: "#f3f4f6",
                    transition: "background-color 0.2s ease",
                },
            },
        },
        pagination: {
            style: {
                fontSize: "12px",
                borderTop: "1px solid #e5e7eb",
                paddingTop: "12px",
                paddingBottom: "12px",
            },
        },
    }), []);

    // Tampilkan error jika ada
    if (error) {
        return (
            <div className="md:ml-64">
                <div className="px-3 md:px-8 h-auto mt-32">
                    <div className="container mx-auto max-w-full">
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            Error: {error.message}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <ModalBayar
                show={bayarModal.show}
                dokumenId={bayarModal.id}
                nominal={bayarModal.nominal}
                tanggalDokumen={bayarModal.tanggalDokumen}
                busy={bayarBusy}
                onSimpan={handleSimpanBayar}
                onBatal={() => setBayarModal({ show: false, id: "", nominal: "" })}
            />
            <div className="md:ml-64">
                <div
                    style={{ background: Colors.primary }}
                    className="pt-14 pb-16 px-3 md:px-8 h-auto"
                >
                    <div className="container mx-auto max-w-full">
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
                    </div>
                </div>
                <div className="px-3 md:px-8 h-auto -mt-32">
                    <div className="container mx-auto max-w-full">
                        <div className="grid grid-cols-1 px-4">
                            <Card className="shadow-lg">
                                <div className="p-6">
                                    {/* Filter Section */}
                                    <div className="mb-6">
                                        <div className="flex flex-wrap gap-4 items-end">
                                            {/* Periode */}
                                            <div className="w-full md:w-64">
                                                <HrdInputGenerator
                                                    type={"bulan"}
                                                    name={"Bulan"}
                                                    title={"Periode"}
                                                    value={values}
                                                    required={true}
                                                    onChange={customHandler}
                                                    touched={touched}
                                                    errors={errors}
                                                    size={"full"}
                                                />
                                            </div>

                                            {/* Bisnis Unit Info */}
                                            <div className="w-full md:w-64">
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                                    <div className="flex items-center">
                                                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                                        <span className="text-sm font-medium text-blue-700">Head Office</span>
                                                    </div>
                                                    <div className="text-xs text-blue-600 mt-1">Bisnis Unit: U0001</div>
                                                </div>
                                            </div>

                                            {/* Filter Button */}
                                            <div className="flex items-center">
                                                <button
                                                    type="button"
                                                    onClick={handleFilter}
                                                    className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                                                >
                                                    Buka Data HO
                                                </button>
                                            </div>

                                            {/* Refresh Button */}
                                            <div className="flex items-center">
                                                <button
                                                    type="button"
                                                    onClick={handleRefresh}
                                                    disabled={isLoadingData}
                                                    className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-500 text-white hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                                >
                                                    <BiRefresh
                                                        className={`inline mr-1 ${isFetching ? "animate-spin" : ""}`}
                                                    />
                                                    Refresh
                                                </button>
                                            </div>

                                            {/* Export Button */}
                                            <div className="flex items-center">
                                                {renderExportButton()}
                                            </div>

                                            {/* Info Loading */}
                                            {isFetching && !isFirstLoad && (
                                                <div className="flex items-center">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                                                    <span className="ml-2 text-sm text-gray-500">
                                                        Memuat data...
                                                    </span>
                                                </div>
                                            )}

                                            {/* Info Total Data */}
                                            {totalRows > 0 && (
                                                <div className="text-sm text-gray-600">
                                                    Total: {totalRows} data
                                                    {searchQuery && ` (hasil pencarian "${searchQuery}")`}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Info Periode yang Ditampilkan */}
                                    <div className="mb-4 px-4 py-2 bg-blue-50 border rounded-md">
                                        <p className="text-sm text-blue-700">
                                            Menampilkan periode:{" "}
                                            <strong>
                                                Bulan {periode.bulan} Tahun {periode.tahun}
                                            </strong>{" "}
                                            | Bisnis Unit: <strong>Head Office (U0001)</strong>
                                            {searchQuery && (
                                                <span className="ml-2">
                                                    | Pencarian: <strong>"{searchQuery}"</strong>
                                                </span>
                                            )}
                                        </p>
                                    </div>

                                    {/* Search Bar */}
                                    <div className="mb-4">
                                        <div className="relative w-full md:w-96">
                                            <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Cari KAS BANK ID, Kepada, Keterangan, Status..."
                                                value={searchInput}
                                                onChange={handleSearchChange}
                                                className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                                            />
                                            {searchInput && (
                                                <button
                                                    onClick={handleClearSearch}
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                    title="Clear search"
                                                >
                                                    ✕
                                                </button>
                                            )}

                                            {isSearching && (
                                                <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                                                    <div className="flex items-center space-x-1">
                                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {searchInput !== searchQuery && searchInput && (
                                            <p className="text-xs text-blue-500 mt-1 flex items-center">
                                                <span className="animate-pulse mr-1">●</span>
                                                Mengetik "{searchInput}"...
                                            </p>
                                        )}

                                        {searchQuery && !isSearching && (
                                            <p className="text-xs text-green-600 mt-1 flex items-center">
                                                <span className="mr-1">✓</span>
                                                Menampilkan hasil untuk "{searchQuery}"
                                            </p>
                                        )}
                                    </div>

                                    {/* Table Section */}
                                    {isLoadingData && isFirstLoad ? (
                                        <div className="w-full">
                                            <Skeltons />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="overflow-x-auto border rounded-lg shadow-sm">
                                                <DataTable
                                                    columns={columns}
                                                    data={tableData}
                                                    customStyles={customStyles}
                                                    pagination
                                                    paginationServer
                                                    paginationTotalRows={totalRows}
                                                    onChangePage={handlePageChange}
                                                    onChangeRowsPerPage={handlePerRowsChange}
                                                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                    paginationPerPage={limit}
                                                    paginationDefaultPage={page}
                                                    paginationResetDefaultPage={page === 1}
                                                    highlightOnHover
                                                    striped
                                                    dense
                                                    progressPending={isLoadingData}
                                                    progressComponent={
                                                        <div className="flex flex-col justify-center items-center py-20">
                                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                                                            <span className="text-gray-600">
                                                                {searchQuery ? `Mencari "${searchQuery}"...` : "Memuat data Patty Cash HO..."}
                                                            </span>
                                                        </div>
                                                    }
                                                    noDataComponent={
                                                        <div className="text-center py-8">
                                                            <div className="text-gray-500 mb-2">
                                                                {searchQuery
                                                                    ? `Tidak ada data yang cocok dengan pencarian "${searchQuery}"`
                                                                    : totalRows === 0
                                                                        ? "Tidak ada data. Klik 'Buka Data HO' untuk memuat."
                                                                        : "Tidak ada data yang ditemukan"}
                                                            </div>
                                                            {searchQuery && (
                                                                <button
                                                                    onClick={handleClearSearch}
                                                                    className="text-main-100 hover:text-main-200 font-medium text-sm"
                                                                >
                                                                    Tampilkan semua data
                                                                </button>
                                                            )}
                                                        </div>
                                                    }
                                                    persistTableHead
                                                    fixedHeader
                                                    fixedHeaderScrollHeight="calc(100vh - 400px)"
                                                    responsive
                                                />
                                            </div>

                                            {/* Info tambahan */}
                                            {totalRows > 0 && (
                                                <div className="mt-4 text-sm text-gray-600">
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            Menampilkan halaman {page} dari{" "}
                                                            {Math.ceil(totalRows / limit)} halaman
                                                            {searchQuery &&
                                                                ` (${totalRows} data ditemukan untuk "${searchQuery}")`}
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <span className="px-3 py-1 bg-gray-50 text-gray-700 rounded-full text-xs">
                                                                Periode: Bulan {periode.bulan} Tahun {periode.tahun}
                                                            </span>
                                                            <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                                                                HO (U0001)
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MonitoringPattyCashHO;